import React from "react";
import { Paper, Box, Typography, Divider } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

type Props = {
  /** Text, or any node when the heading needs adornment (e.g. a status chip). */
  title: React.ReactNode;
  subtitle?: string;
  rightAction?: React.ReactNode;
  /** Overrides for the card itself, e.g. to tint one section apart. */
  sx?: SxProps<Theme>;
  children: React.ReactNode;
};

const Section: React.FC<Props> = ({
  title,
  subtitle,
  rightAction,
  sx,
  children,
}) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 1.75, sm: 2.5 },
      mb: { xs: 2.5, sm: 4 },
      borderRadius: 2,
      borderColor: "divider",
      bgcolor: "background.paper",
      ...sx,
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: { xs: "stretch", sm: "flex-start" },
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1, sm: 2 },
        mb: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {rightAction && (
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            flexShrink: 0,
          }}
        >
          {rightAction}
        </Box>
      )}
    </Box>
    <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
    {children}
  </Paper>
);

export default Section;
